/**
 * The type controls shared by Heading and Text.
 *
 * Both blocks pick a named scale for the shape of the thing, then optionally override the
 * size, weight, slant and underline. Declaring that once here is what stops the two drifting
 * apart the way the button sizes did.
 */

import type { FieldSpec, SelectOption } from '@lp/shared';
import { SPACE_BELOW_STEPS, TYPE_SIZES, TYPE_WEIGHTS } from '@lp/tokens';

export interface TypeStyleProps {
  /** A step from `TYPE_SIZES`, or `''` to keep the scale's own size. */
  size: string;
  /** A weight from `TYPE_WEIGHTS`, or `''` to keep the scale's own weight. */
  weight: string;
  italic: boolean;
  underline: boolean;
  /** A spacing step added below this element, on top of the container's gap. `'0'` for none. */
  spaceBelow: string;
}

export const TYPE_STYLE_DEFAULTS: TypeStyleProps = {
  size: '',
  weight: '',
  italic: false,
  underline: false,
  spaceBelow: '0',
};

const sizeOptions: SelectOption[] = [
  { label: 'Default for this style', value: '' },
  ...TYPE_SIZES.map((s) => ({ label: `${s}px`, value: String(s) })),
];

const weightOptions: SelectOption[] = [
  { label: 'Default for this style', value: '' },
  ...TYPE_WEIGHTS.map((w) => ({ label: `${w.label} (${w.value})`, value: w.value })),
];

/** Labelled in pixels, because "is 24px enough under this heading?" is a visual question. */
const SPACE_PX: Record<string, string> = {
  '0': 'None',
  '1': '4px',
  '2': '8px',
  '3': '12px',
  '4': '16px',
  '5': '24px',
  '6': '32px',
  '7': '48px',
  '8': '64px',
};

const spaceBelowOptions: SelectOption[] = SPACE_BELOW_STEPS.map((s) => ({
  label: SPACE_PX[String(s)] ?? `${s}`,
  value: String(s),
}));

/** Spread into a block's `fields`. */
export const typeStyleFields: Record<string, FieldSpec> = {
  size: {
    type: 'select',
    label: 'Size override',
    help: 'Overrides the size for this one element. Large sizes shrink on a phone automatically.',
    options: sizeOptions,
  },
  weight: { type: 'select', label: 'Weight', options: weightOptions },
  italic: { type: 'boolean', label: 'Italic' },
  underline: { type: 'boolean', label: 'Underline' },
  spaceBelow: {
    type: 'select',
    label: 'Extra space below',
    // Additive, and the label has to say so: the container's gap is still there underneath.
    // To close a gap rather than open one, lower the gap on the Section, Box or Column.
    help: 'Added to the container’s gap. To reduce a gap instead, lower the container’s Gap.',
    options: spaceBelowOptions,
  },
};

/** `lp-body` → `body`, the suffix every text combo is named with. */
const suffix = (base: string): string => base.replace('lp-', '');

/**
 * The override combos for one base. Empty values contribute nothing, so an element that takes
 * the scale as-is carries exactly the classes it did before these controls existed.
 */
export function typeStyleCombos(
  base: string,
  props: Partial<TypeStyleProps>,
): Array<string | null> {
  const s = suffix(base);
  return [
    props.size ? `is-size-${props.size}-${s}` : null,
    props.weight ? `is-weight-${props.weight}-${s}` : null,
    props.italic ? `is-italic-${s}` : null,
    props.underline ? `is-underline-${s}` : null,
    props.spaceBelow && props.spaceBelow !== '0' ? `is-mb-${props.spaceBelow}-${s}` : null,
  ];
}
